'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { ChevronLeft, ChevronDown, ChevronUp } from 'lucide-react';

interface Section {
  number: string;
  title: string;
  content: React.ReactNode;
}

const sections: Section[] = [
  {
    number: '1',
    title: 'Geltungsbereich',
    content: (
      <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
        Diese Allgemeinen Geschäftsbedingungen gelten für die Nutzung der Plattform WINEWOOD Adventure sowie der dazugehörigen Website und App. Mit der Registrierung oder Nutzung akzeptieren Nutzer diese Bedingungen.
      </p>
    ),
  },
  {
    number: '2',
    title: 'Leistungsbeschreibung',
    content: (
      <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
        WINEWOOD Adventure ist eine Plattform zur Vermittlung und Darstellung von Freizeitangeboten sowie GPS-basierten Erlebnissen. Nutzer können Angebote entdecken, Abenteuer absolvieren und an von WINEWOOD Adventure organisierten Aktionen teilnehmen.
      </p>
    ),
  },
  {
    number: '3',
    title: 'Nutzerkonto',
    content: (
      <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
        Für bestimmte Funktionen ist ein Nutzerkonto erforderlich. Nutzer sind verpflichtet, ihre Zugangsdaten vertraulich zu behandeln und wahrheitsgemäße Angaben zu machen.
      </p>
    ),
  },
  {
    number: '4',
    title: 'WINEWOOD+',
    content: (
      <div className="space-y-3">
        <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
          WINEWOOD+ ist ein kostenpflichtiges Abonnement mit monatlicher Laufzeit. Leistungen können unter anderem umfassen:
        </p>
        <ul className="space-y-1.5 pl-4">
          {['Exklusive Rabatte', 'Vergünstigte Angebote', 'Weitere Premium-Funktionen'].map((item) => (
            <li key={item} className="text-sm flex items-start gap-2" style={{ color: 'var(--muted-foreground)' }}>
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: 'var(--primary)' }} />
              {item}
            </li>
          ))}
        </ul>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
          Die jeweils aktuellen Leistungen und Preise werden innerhalb der App angezeigt. Das Abonnement beginnt unmittelbar nach erfolgreicher Zahlung. Die Kündigung ist jederzeit zum Ende des laufenden Abrechnungszeitraums möglich.
        </p>
      </div>
    ),
  },
  {
    number: '5',
    title: 'Widerrufsrecht bei digitalen Leistungen',
    content: (
      <div className="space-y-2">
        <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
          Verbrauchern steht grundsätzlich ein gesetzliches Widerrufsrecht von 14 Tagen zu.
        </p>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
          Mit Abschluss des Abonnements stimmt der Nutzer ausdrücklich zu, dass WINEWOOD Adventure bereits vor Ablauf der Widerrufsfrist mit der Ausführung der digitalen Leistung beginnt.
        </p>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
          Bereits erbrachte Leistungen können im Falle eines Widerrufs anteilig berechnet werden (§ 357a BGB). Soweit das gesetzlich zulässig ist, erlischt das Widerrufsrecht, wenn die digitale Leistung vollständig erbracht wurde und der Nutzer zuvor ausdrücklich zugestimmt hat.
        </p>
      </div>
    ),
  },
  {
    number: '6',
    title: 'Partner-Konten',
    content: (
      <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
        Unternehmen können nach Freischaltung durch den Betreiber Partnerkonten erhalten und eigene Angebote veröffentlichen. Partnerkonten sind kostenpflichtig.
      </p>
    ),
  },
  {
    number: '7',
    title: 'Rabatte und Promo-Codes',
    content: (
      <div className="space-y-2">
        <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
          Rabatte und Gutscheine gelten ausschließlich nach den jeweiligen Aktionsbedingungen.
        </p>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
          Einige Vorteile setzen ein aktives WINEWOOD+-Abonnement voraus. Promo-Codes können auf einen Nutzer beschränkt oder nur einmal einlösbar sein.
        </p>
      </div>
    ),
  },
  {
    number: '8',
    title: 'Haftung',
    content: (
      <div className="space-y-2">
        <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
          WINEWOOD Adventure vermittelt teilweise Angebote Dritter. Für Inhalt, Qualität, Durchführung oder Verfügbarkeit dieser Angebote sind ausschließlich die jeweiligen Anbieter verantwortlich.
        </p>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
          Für Vorsatz und grobe Fahrlässigkeit haftet WINEWOOD Adventure nach den gesetzlichen Vorschriften.
        </p>
      </div>
    ),
  },
  {
    number: '9',
    title: 'Änderungen',
    content: (
      <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
        WINEWOOD Adventure behält sich vor, diese AGB anzupassen, soweit hierfür berechtigte Gründe bestehen. Nutzer werden über wesentliche Änderungen informiert.
      </p>
    ),
  },
];

function AGBSection({ section }: { section: Section }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="rounded-app overflow-hidden"
      style={{ border: '1px solid var(--border)' }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors duration-150"
        style={{ background: open ? 'var(--card-elevated)' : 'var(--card)' }}
        aria-expanded={open}
      >
        <span
          className="text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: 'rgba(124,179,66,0.15)', color: 'var(--primary)' }}
        >
          {section.number}
        </span>
        <span className="flex-1 text-sm font-medium" style={{ color: 'var(--card-foreground)' }}>
          {section.title}
        </span>
        <span style={{ color: 'var(--muted-foreground)' }}>
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-2" style={{ background: 'var(--card)' }}>
          {section.content}
        </div>
      )}
    </div>
  );
}

export default function AGBPage() {
  return (
    <div className="min-h-screen pb-12" style={{ background: 'var(--background)', color: 'var(--foreground)' }}>
      {/* Header */}
      <div
        className="sticky top-0 z-10 flex items-center gap-3 px-4"
        style={{ background: 'rgba(27,29,35,0.96)', borderBottom: '1px solid var(--border)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', paddingTop: 'calc(env(safe-area-inset-top, 44px) + 12px)', paddingBottom: '12px' }}
      >
        <Link
          href="/profile"
          className="p-2 rounded-full transition-colors active:scale-95"
          style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}
          aria-label="Zurück"
        >
          <ChevronLeft size={18} />
        </Link>
        <h1 className="text-base font-semibold" style={{ color: 'var(--card-foreground)' }}>Allgemeine Geschäftsbedingungen</h1>
      </div>

      {/* Content */}
      <div className="max-w-mobile mx-auto px-4 pt-6 space-y-3">
        <p className="text-xs px-1 mb-5" style={{ color: 'var(--muted-foreground)' }}>
          Tippe auf einen Abschnitt, um ihn zu öffnen.
        </p>
        {sections.map((section) => (
          <AGBSection key={section.number} section={section} />
        ))}
      </div>
    </div>
  );
}
